import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { isSpecimenId, type SpecimenId } from '../../shared/specimens'
import { LoopTrace, RunJsWidget, RuntimeProbe } from './specimens/Enginehouse'

/**
 * Zone 3 — the live specimen.
 *
 * Each widget calls a REAL endpoint of this site. Not a fixture, not a
 * recording. When the whoami widget tells you which Cloudflare data centre
 * served you, that is because a request genuinely travelled there and back
 * while you watched.
 */
/**
 * The router, as an exhaustive map rather than a ternary chain.
 *
 * `Record<SpecimenId, ...>` is the load-bearing part: adding an id to
 * shared/specimens.ts without adding it here is a compile error. Before this,
 * the content linter checked ids against a copy of the list, which caught a
 * typo in a lesson but NOT the other failure — an id that lints fine and has no
 * renderer, which drops the specimen zone from the page with no error anywhere.
 */
const WIDGETS: Record<SpecimenId, () => JSX.Element> = {
  whoami: WhoAmI,
  echo: Echo,
  status: StatusCodes,
  sql: SqlExplorer,
  search: SearchExplorer,
  runtime: RuntimeProbe,
  'run-js': RunJsWidget,
  'loop-trace': LoopTrace,
}

export default function Specimen({ id }: { id: string | null }) {
  if (!id || !isSpecimenId(id)) return null

  const Widget = WIDGETS[id]
  const widget = <Widget />

  return (
    <section className="zone zone-specimen">
      <h3 className="zone-heading">
        <span className="zone-num">3</span> Specimen
        <small className="dim"> — live, against this site</small>
      </h3>
      {widget}
    </section>
  )
}

function WhoAmI() {
  const [data, setData] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  return (
    <div className="specimen-widget">
      <p>
        Ask the server what it can see about you before your code runs. Everything below arrives
        with the request itself — none of it is stored.
      </p>
      <button
        type="button"
        className="button"
        disabled={busy}
        onClick={() => {
          setBusy(true)
          api('/meta/whoami')
            .then(setData)
            .finally(() => setBusy(false))
        }}
      >
        {busy ? 'Asking…' : 'GET /api/meta/whoami'}
      </button>
      {data != null && <pre className="specimen-out">{JSON.stringify(data, null, 2)}</pre>}
    </div>
  )
}

function Echo() {
  const [method, setMethod] = useState('POST')
  const [body, setBody] = useState('{\n  "hello": "world"\n}')
  const [data, setData] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  async function send() {
    setBusy(true)
    try {
      let parsed: unknown = undefined
      if (method !== 'GET') {
        try {
          parsed = JSON.parse(body)
        } catch {
          parsed = body
        }
      }
      setData(await api('/meta/echo', { method, body: parsed }))
    } catch (e) {
      setData({ error: (e as Error).message })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="specimen-widget">
      <p>Send anything. The server describes exactly what arrived.</p>
      <div className="specimen-controls">
        <select value={method} onChange={(e) => setMethod(e.target.value)} aria-label="HTTP method">
          {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
            <option key={m}>{m}</option>
          ))}
        </select>
        <button type="button" className="button" onClick={() => void send()} disabled={busy}>
          {busy ? 'Sending…' : 'Send'}
        </button>
      </div>
      {method !== 'GET' && (
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={5}
          spellCheck={false}
          aria-label="Request body"
        />
      )}
      {data != null && <pre className="specimen-out">{JSON.stringify(data, null, 2)}</pre>}
    </div>
  )
}

interface QueryResult {
  id: string
  title: string
  teaches: string
  sql: string
  rows: Record<string, unknown>[]
  rowCount: number
  ms: number
  plan: string[]
  rowsRead: number | null
}

/**
 * The SQL explorer.
 *
 * Runs real queries against the real database this site uses, and shows the
 * query plan alongside the results. Two of the presets return identical rows
 * with completely different plans — that pair is the whole index lesson.
 */
function SqlExplorer() {
  const [catalogue, setCatalogue] = useState<{ id: string; title: string }[]>([])
  const [result, setResult] = useState<QueryResult | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api<{ queries: { id: string; title: string }[] }>('/meta/queries')
      .then((d) => setCatalogue(d.queries))
      .catch(() => {})
  }, [])

  function run(id: string) {
    setBusy(true)
    api<QueryResult>(`/meta/queries/${id}`)
      .then(setResult)
      .finally(() => setBusy(false))
  }

  const usesIndex = result?.plan.some((p) => /USING (COVERING )?INDEX|USING INTEGER PRIMARY KEY/.test(p))
  const scans = result?.plan.some((p) => /^SCAN/.test(p))

  return (
    <div className="specimen-widget">
      <p>
        Real queries against the database serving this page. Watch the{' '}
        <strong>query plan</strong> — two of these return identical rows and reach them completely
        differently.
      </p>

      <div className="query-list">
        {catalogue.map((q) => (
          <button
            key={q.id}
            type="button"
            className={result?.id === q.id ? 'button button-small button-primary' : 'button button-small'}
            onClick={() => run(q.id)}
            disabled={busy}
          >
            {q.title}
          </button>
        ))}
      </div>

      {result && (
        <div className="query-result">
          <p className="dim query-teaches">{result.teaches}</p>
          <pre className="code-block">{result.sql}</pre>

          <div className="query-stats">
            <span>
              <strong>{result.rowCount}</strong> rows
            </span>
            <span>
              <strong>{result.ms}</strong> ms
            </span>
            {result.rowsRead !== null && (
              <span title="Rows SQLite actually had to read to answer">
                <strong>{result.rowsRead}</strong> rows read
              </span>
            )}
            {usesIndex && <span className="plan-good">index used</span>}
            {scans && !usesIndex && <span className="plan-bad">full scan</span>}
          </div>

          <h4>Query plan</h4>
          <pre className={scans && !usesIndex ? 'query-plan plan-scan' : 'query-plan'}>
            {result.plan.join('\n')}
          </pre>

          <h4>Results</h4>
          {result.rows.length === 0 ? (
            <p className="dim">No rows.</p>
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    {Object.keys(result.rows[0]).map((col) => (
                      <th key={col}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {result.rows.slice(0, 20).map((row, i) => (
                    <tr key={i}>
                      {Object.values(row).map((val, j) => (
                        <td key={j}>{val === null ? <span className="dim">null</span> : String(val)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {result.rows.length > 20 && <p className="dim">Showing 20 of {result.rowCount} rows.</p>}
        </div>
      )}
    </div>
  )
}

interface SearchResult {
  score: number
  snippet: string
  heading: string | null
  lessonTitle: string
  moduleSlug: string
  lessonSlug: string
  zone: string
}
interface SearchResponse {
  query: string
  terms: string[]
  model: string
  corpusSize: number
  latencyMs: number
  confident: boolean
  results: SearchResult[]
  empty?: boolean
  message?: string
}

/**
 * The semantic search box.
 *
 * Real embeddings, computed live, scored against every chunk of this site's
 * own content by cosine similarity — a working vector database, built by hand,
 * doing its job in front of you.
 */
function SearchExplorer() {
  const [q, setQ] = useState('why does my query read every row instead of jumping to the answer')
  const [data, setData] = useState<SearchResponse | null>(null)
  const [busy, setBusy] = useState(false)

  function run() {
    if (q.trim().length < 2) return
    setBusy(true)
    api<SearchResponse>('/search', { method: 'POST', body: { q } })
      .then(setData)
      .finally(() => setBusy(false))
  }

  return (
    <div className="specimen-widget">
      <p>
        Search this site's own content by meaning, not keywords. Try the example below, then
        replace it with something that shares no words with the lesson you expect to find.
      </p>
      <div className="specimen-controls">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && run()}
          placeholder="Ask a question in your own words…"
          aria-label="Search query"
        />
        <button type="button" className="button button-primary" onClick={run} disabled={busy}>
          {busy ? 'Searching…' : 'Search'}
        </button>
      </div>

      {data && (
        <div className="search-out">
          {data.empty ? (
            <p className="dim">{data.message}</p>
          ) : (
            <>
              <div className="search-meta">
                <span>
                  extracted terms: <code>{data.terms.join(', ')}</code>
                </span>
                <span>
                  {data.corpusSize} chunks scored in {data.latencyMs}ms
                </span>
                <span className={data.confident ? 'plan-good' : 'plan-bad'}>
                  {data.confident ? 'confident match' : 'weak match — would refuse to answer'}
                </span>
              </div>

              {data.results.length === 0 ? (
                <p className="dim">Nothing indexed. Run the embedding pipeline first.</p>
              ) : (
                <ol className="search-results">
                  {data.results.map((r) => (
                    <li key={r.lessonSlug + r.snippet.slice(0, 10)}>
                      <div className="search-result-head">
                        <span className="search-score">{r.score.toFixed(3)}</span>
                        <Link to={`/floor/${r.moduleSlug}/${r.lessonSlug}`}>{r.lessonTitle}</Link>
                        <span className="dim">{r.zone}</span>
                      </div>
                      <p className="search-snippet">{r.snippet}</p>
                    </li>
                  ))}
                </ol>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}

function StatusCodes() {
  const [data, setData] = useState<unknown>(null)
  const codes = [200, 201, 204, 301, 400, 401, 403, 404, 409, 418, 429, 500, 503]

  return (
    <div className="specimen-widget">
      <p>Ask for a status code and see what it actually means in practice.</p>
      <div className="code-grid">
        {codes.map((code) => (
          <button
            key={code}
            type="button"
            className="button button-small"
            onClick={() => {
              api(`/meta/status/${code}`)
                .then(setData)
                .catch((e: Error) => setData({ note: 'A non-2xx threw, as it should.', error: e.message }))
            }}
          >
            {code}
          </button>
        ))}
      </div>
      {data != null && <pre className="specimen-out">{JSON.stringify(data, null, 2)}</pre>}
    </div>
  )
}
